import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { stripeService } from '../services/stripeService';

type WelcomeStep = 'LOADING' | 'RESOLVED' | 'AUTHENTICATED' | 'LINKING' | 'DONE' | 'ERROR';

interface CheckoutInfo {
  customer_email: string;
  product_name: string;
  amount_total: number;
  currency: string;
  account_exists: boolean;
  has_course: boolean;
  already_claimed: boolean;
}

export const StripeWelcomePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { user, login, register } = useAuth();

  const [step, setStep] = useState<WelcomeStep>('LOADING');
  const [checkoutInfo, setCheckoutInfo] = useState<CheckoutInfo | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [alreadyClaimed, setAlreadyClaimed] = useState(false);
  const [formMode, setFormMode] = useState<'login' | 'register'>('register');
  const [formError, setFormError] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);

  // Claim result
  const [claimProductName, setClaimProductName] = useState('');
  const [claimHasCourse, setClaimHasCourse] = useState(false);

  // Form fields
  const [nameField, setNameField] = useState('');
  const [emailField, setEmailField] = useState('');
  const [usernameField, setUsernameField] = useState('');
  const [passwordField, setPasswordField] = useState('');
  const [confirmPasswordField, setConfirmPasswordField] = useState('');

  // Prevent double-claim
  const claimTriggeredRef = useRef(false);

  const sessionId = searchParams.get('session_id');
  const token = searchParams.get('token');

  const formatAmount = (amount: number, currency: string): string => {
    return (amount / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    });
  };

  // Get the param object for API calls
  const getParams = useCallback((): { session_id?: string; token?: string } => {
    const params: { session_id?: string; token?: string } = {};
    if (sessionId) params.session_id = sessionId;
    if (token) params.token = token;
    return params;
  }, [sessionId, token]);

  // Perform the claim
  const performClaim = useCallback(async (): Promise<void> => {
    if (claimTriggeredRef.current) return;
    claimTriggeredRef.current = true;

    setStep('LINKING');
    try {
      const result = await stripeService.welcomeClaim(getParams());
      setClaimProductName(result.product_name);
      setClaimHasCourse(result.has_course);
      setStep('DONE');
    } catch (err: unknown) {
      setStep('ERROR');
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setErrorMessage(axiosErr?.response?.data?.error || 'Something went wrong. Please try again.');
    }
  }, [getParams]);

  // Step 1: LOADING — fetch checkout info on mount
  useEffect(() => {
    if (!sessionId && !token) {
      setStep('ERROR');
      setErrorMessage('No session ID or token provided. Check your email for a valid link.');
      return;
    }

    let cancelled = false;

    stripeService
      .getCheckoutInfo(getParams())
      .then((info) => {
        if (cancelled) return;
        setCheckoutInfo(info);

        if (info.already_claimed) {
          setAlreadyClaimed(true);
          setStep('ERROR');
          return;
        }

        if (info.account_exists) {
          setFormMode('login');
        } else {
          setFormMode('register');
          setEmailField(info.customer_email || '');
        }
        setStep('RESOLVED');
      })
      .catch(() => {
        if (cancelled) return;
        setStep('ERROR');
        setErrorMessage('This link has expired. Check your email for a newer link, or contact support.');
      });

    return () => {
      cancelled = true;
    };
  }, [sessionId, token, getParams]);

  // Auto-trigger claim if user is already logged in when we reach RESOLVED
  useEffect(() => {
    if (step === 'RESOLVED' && user) {
      performClaim();
    }
  }, [step, user, performClaim]);

  // Auto-redirect after DONE
  useEffect(() => {
    if (step !== 'DONE') return;

    const timer = setTimeout(() => {
      window.location.href = '/';
    }, 3000);

    return () => clearTimeout(timer);
  }, [step]);

  const handleLogin = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setFormError('');
    setFormSubmitting(true);

    try {
      await login({ username: usernameField, password: passwordField });
      setStep('AUTHENTICATED');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { non_field_errors?: string[]; detail?: string } } };
      const data = axiosErr?.response?.data;
      setFormError(
        data?.non_field_errors?.[0] || data?.detail || 'Login failed. Check your credentials.'
      );
      setFormSubmitting(false);
    }
  };

  const handleRegister = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setFormError('');

    if (passwordField !== confirmPasswordField) {
      setFormError('Passwords do not match.');
      return;
    }

    setFormSubmitting(true);

    try {
      await register({
        name: nameField,
        email: emailField,
        username: usernameField,
        password: passwordField,
        password_confirm: confirmPasswordField,
      });
      setStep('AUTHENTICATED');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: Record<string, string[]> } };
      const data = axiosErr?.response?.data;
      if (data && typeof data === 'object') {
        // Show field-specific errors
        const messages: string[] = [];
        for (const [, values] of Object.entries(data)) {
          if (Array.isArray(values)) {
            messages.push(...values);
          }
        }
        setFormError(messages[0] || 'Registration failed.');
      } else {
        setFormError('Registration failed.');
      }
      setFormSubmitting(false);
    }
  };

  // AUTHENTICATED step: auto-trigger claim
  useEffect(() => {
    if (step === 'AUTHENTICATED') {
      performClaim();
    }
  }, [step, performClaim]);

  // --- Render ---

  const inputClasses =
    'w-full bg-dark-bg border border-dark-border rounded-lg px-4 py-3 text-white focus:border-accent-yellow focus:outline-none';
  const buttonClasses =
    'w-full bg-accent-yellow text-dark-bg font-semibold py-3 rounded-lg hover:bg-yellow-400 disabled:opacity-50 transition-colors';

  return (
    <div className="min-h-screen bg-dark-bg flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* LOADING */}
        {step === 'LOADING' && (
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-accent-yellow mx-auto mb-4" />
            <p className="text-sm text-gray-400">Loading purchase details...</p>
          </div>
        )}

        {/* LINKING */}
        {step === 'LINKING' && (
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-accent-yellow mx-auto mb-4" />
            <h2 className="text-lg font-medium text-gray-100">Linking your purchase...</h2>
            <p className="text-sm text-gray-400 mt-2">Please wait.</p>
          </div>
        )}

        {/* DONE */}
        {step === 'DONE' && (
          <div className="bg-dark-surface rounded-lg border border-dark-border p-8 text-center">
            <svg
              className="w-12 h-12 text-emerald-500 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <h2 className="text-xl font-semibold text-gray-100">Your account is set up.</h2>
            <p className="text-sm text-gray-400 mt-2">
              {claimProductName} has been linked to your account.
            </p>
            {claimHasCourse && (
              <p className="text-sm text-accent-yellow mt-3">
                You have a course to claim — head to Courses.
              </p>
            )}
            <p className="text-xs text-gray-500 mt-4">Redirecting to your dashboard...</p>
          </div>
        )}

        {/* ERROR */}
        {step === 'ERROR' && (
          <div className="bg-dark-surface rounded-lg border border-dark-border p-8 text-center">
            <svg
              className="w-12 h-12 text-red-400 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
            {alreadyClaimed ? (
              <>
                <h2 className="text-lg font-medium text-gray-100">Already Claimed</h2>
                <p className="text-sm text-gray-400 mt-2">
                  This purchase is already linked to an account.
                </p>
                <a
                  href="/"
                  className="inline-block mt-6 px-4 py-2 bg-dark-bg border border-dark-border text-gray-300 text-sm rounded-lg hover:bg-dark-border transition-colors"
                >
                  Go to Dashboard
                </a>
              </>
            ) : (
              <>
                <h2 className="text-lg font-medium text-gray-100">Something Went Wrong</h2>
                <p className="text-sm text-gray-400 mt-2">{errorMessage}</p>
                <a
                  href="/"
                  className="inline-block mt-6 px-4 py-2 bg-dark-bg border border-dark-border text-gray-300 text-sm rounded-lg hover:bg-dark-border transition-colors"
                >
                  Go to Dashboard
                </a>
              </>
            )}
          </div>
        )}

        {/* RESOLVED — show auth form */}
        {step === 'RESOLVED' && checkoutInfo && !user && (
          <div className="bg-dark-surface rounded-lg border border-dark-border p-8">
            {/* Purchase summary */}
            <div className="text-center mb-6">
              <h1 className="text-xl font-semibold text-gray-100">
                Welcome to FZN Trading!
              </h1>
              <p className="text-sm text-gray-400 mt-2">
                You purchased{' '}
                <span className="text-gray-200 font-medium">{checkoutInfo.product_name}</span>
                {checkoutInfo.amount_total > 0 && (
                  <> for {formatAmount(checkoutInfo.amount_total, checkoutInfo.currency)}</>
                )}
              </p>
            </div>

            {/* Tab switcher */}
            <div className="flex border-b border-dark-border mb-6">
              <button
                onClick={() => {
                  setFormMode('register');
                  setFormError('');
                }}
                className={`flex-1 pb-2 text-sm font-medium border-b-2 transition-colors ${
                  formMode === 'register'
                    ? 'border-accent-yellow text-accent-yellow'
                    : 'border-transparent text-gray-500 hover:text-gray-400'
                }`}
              >
                Create Account
              </button>
              <button
                onClick={() => {
                  setFormMode('login');
                  setFormError('');
                }}
                className={`flex-1 pb-2 text-sm font-medium border-b-2 transition-colors ${
                  formMode === 'login'
                    ? 'border-accent-yellow text-accent-yellow'
                    : 'border-transparent text-gray-500 hover:text-gray-400'
                }`}
              >
                {checkoutInfo.account_exists ? 'Welcome Back — Sign In' : 'Sign In'}
              </button>
            </div>

            {/* Login form */}
            {formMode === 'login' && (
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Username</label>
                  <input
                    type="text"
                    value={usernameField}
                    onChange={(e) => setUsernameField(e.target.value)}
                    className={inputClasses}
                    required
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Password</label>
                  <input
                    type="password"
                    value={passwordField}
                    onChange={(e) => setPasswordField(e.target.value)}
                    className={inputClasses}
                    required
                  />
                </div>
                {formError && <p className="text-sm text-red-400">{formError}</p>}
                <button type="submit" disabled={formSubmitting} className={buttonClasses}>
                  {formSubmitting ? 'Signing in...' : 'Sign In & Activate'}
                </button>
              </form>
            )}

            {/* Register form */}
            {formMode === 'register' && (
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Full Name</label>
                  <input
                    type="text"
                    value={nameField}
                    onChange={(e) => setNameField(e.target.value)}
                    className={inputClasses}
                    required
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Email</label>
                  <input
                    type="email"
                    value={emailField}
                    onChange={(e) => setEmailField(e.target.value)}
                    className={inputClasses}
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Username</label>
                  <input
                    type="text"
                    value={usernameField}
                    onChange={(e) => setUsernameField(e.target.value)}
                    className={inputClasses}
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Password</label>
                  <input
                    type="password"
                    value={passwordField}
                    onChange={(e) => setPasswordField(e.target.value)}
                    className={inputClasses}
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Confirm Password</label>
                  <input
                    type="password"
                    value={confirmPasswordField}
                    onChange={(e) => setConfirmPasswordField(e.target.value)}
                    className={inputClasses}
                    required
                  />
                </div>
                {formError && <p className="text-sm text-red-400">{formError}</p>}
                <button type="submit" disabled={formSubmitting} className={buttonClasses}>
                  {formSubmitting ? 'Creating account...' : 'Create Account & Activate'}
                </button>
              </form>
            )}
          </div>
        )}

        {/* AUTHENTICATED — briefly visible before claim starts */}
        {step === 'AUTHENTICATED' && (
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-accent-yellow mx-auto mb-4" />
            <p className="text-sm text-gray-400">Linking your purchase...</p>
          </div>
        )}
      </div>
    </div>
  );
};
